import {type BoundingBox3D} from '@wandb/weave/common/types/media';
import {compare} from '@wandb/weave/common/util/ops';

import {
  type AllBoundingBoxControls,
  type BoundingBoxSliderControl,
} from '../MediaCard';
import {
  BOXES,
  DEFAULT_SCORE_GROUP_NAME,
  GROUP_NAME_3D_BOUNDING_BOXES,
} from '../PanelMediaBrowser/common';
import {type PointCloudPoint, type PointCloudVector} from './types';

/** Boxes only need label/score for visibility; edge shape is renderer-specific. */
export type FilterableBoundingBox = {
  label?: string;
  score?: number;
};

/**
 * Points/vectors use the shared scene types. Boxes stay generic so Babylon
 * `SceneBox` (Vector3 edges) and `PointCloudBox` (plain XYZ) both filter.
 */
export type FilterablePointCloud<TBox extends FilterableBoundingBox> = {
  points: readonly PointCloudPoint[];
  vectors: readonly PointCloudVector[];
  boxes: readonly TBox[];
};

export const getEmptyFilter = () => ({
  hiddenBoundingBoxLabels: [],
  shownBoundingBoxLabels: [],
  hideAllBoxes: false,
  score: {},
});

export type Filter = {
  hiddenBoundingBoxLabels: string[];
  shownBoundingBoxLabels: string[];
  hideAllBoxes: boolean;
  score: BoundingBoxSliderControl;
};

const ALL_LABEL = 'all';

export type ClassIdToLabelMap = Map<number, string>;

export const MAX_BOUNDING_BOX_LABELS_FOR_DISPLAY = 50;

function categorizeBoxVisibility(
  classIdControl: NonNullable<
    AllBoundingBoxControls['toggles']
  >[string][string],
  classIdToLabel: ClassIdToLabelMap
) {
  return Object.keys(classIdControl).reduce<{
    hiddenBoundingBoxLabels: string[];
    shownBoundingBoxLabels: string[];
  }>(
    ({hiddenBoundingBoxLabels, shownBoundingBoxLabels}, strClassId) => {
      if (strClassId === ALL_LABEL) {
        return {hiddenBoundingBoxLabels, shownBoundingBoxLabels};
      }
      const classId = parseInt(strClassId, 10);
      const label = classIdToLabel.get(classId);
      const toggle = classIdControl[classId];
      if (!label || !toggle) {
        return {hiddenBoundingBoxLabels, shownBoundingBoxLabels};
      }
      if (toggle.disabled) {
        hiddenBoundingBoxLabels.push(label);
      } else {
        shownBoundingBoxLabels.push(label);
      }

      return {hiddenBoundingBoxLabels, shownBoundingBoxLabels};
    },
    {
      hiddenBoundingBoxLabels: [] as string[],
      shownBoundingBoxLabels: [] as string[],
    }
  );
}

export const getFilterFromBBoxConfig = (
  boundingBoxConfig: AllBoundingBoxControls | undefined,
  classIdToLabel: ClassIdToLabelMap
): Filter => {
  if (!boundingBoxConfig) {
    return getEmptyFilter();
  }

  const classIdControl =
    boundingBoxConfig?.toggles?.[BOXES]?.[GROUP_NAME_3D_BOUNDING_BOXES] ?? {};

  const {hiddenBoundingBoxLabels, shownBoundingBoxLabels} =
    categorizeBoxVisibility(classIdControl, classIdToLabel);
  return {
    hiddenBoundingBoxLabels,
    shownBoundingBoxLabels,
    hideAllBoxes: classIdControl[ALL_LABEL]?.disabled ?? false,
    score: boundingBoxConfig.sliders
      ? boundingBoxConfig.sliders[DEFAULT_SCORE_GROUP_NAME]
      : {},
  };
};

const isBoundingBoxVisible = (box: FilterableBoundingBox, filter: Filter) => {
  const isBoxManuallyShown =
    box.label != null && filter.shownBoundingBoxLabels.includes(box.label);
  if (filter.hideAllBoxes && !isBoxManuallyShown) {
    return false;
  }

  if (box.label && filter.hiddenBoundingBoxLabels.includes(box.label)) {
    return false;
  }

  if (box.score === undefined) {
    return true;
  }
  if (
    !filter.score ||
    filter.score.value === undefined ||
    filter.score.disabled
  ) {
    return true;
  }
  return compare(
    filter.score.comparator ?? 'gte',
    box.score,
    filter.score.value
  );
};

export const applyFilter = <
  T extends FilterablePointCloud<FilterableBoundingBox>,
>(
  data: T,
  filter: Filter
): T =>
  ({
    ...data,
    points: [...data.points],
    vectors: [...data.vectors],
    boxes: data.boxes.filter(box => isBoundingBoxVisible(box, filter)),
  }) as T;

export function getFilteringOptionsForPointCloud(pointCloud: {
  boxes: Array<{label?: string; score?: number}>;
}): {boxData: BoundingBox3D[]; newClassIdToLabel: ClassIdToLabelMap} {
  const labelToClassId = new Map<string, number>();
  const boxData: BoundingBox3D[] = pointCloud.boxes.reduce<BoundingBox3D[]>(
    (resultSoFar, currentValue) => {
      const {label, score} = currentValue;
      if (!label && score === undefined) {
        return resultSoFar;
      }
      const toAdd: BoundingBox3D = {
        type: '3d',
        score,
      };
      if (label) {
        if (!labelToClassId.has(label)) {
          if (labelToClassId.size < MAX_BOUNDING_BOX_LABELS_FOR_DISPLAY) {
            labelToClassId.set(label, labelToClassId.size);
          } else {
            console.warn(
              'This point cloud had > ',
              MAX_BOUNDING_BOX_LABELS_FOR_DISPLAY,
              ' labels, so ',
              label,
              "won't be available for filtering."
            );
          }
        }
        const id = labelToClassId.get(label);
        if (id === undefined) {
          console.warn(
            'Unable to find filtering id for label:',
            label,
            'skipping this label for filtering'
          );
          if (!score) {
            return resultSoFar;
          }
        } else {
          toAdd.classInfo = {label, id};
        }
      }

      return resultSoFar.concat(toAdd);
    },
    []
  );
  return {
    boxData,
    newClassIdToLabel: new Map<number, string>(
      Array.from(labelToClassId.entries()).map(([str, num]) => [num, str])
    ),
  };
}
